import React from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, ChevronRight, Info, WifiOff, RefreshCw, AlertTriangle, Lightbulb, ExternalLink } from "lucide-react";
import apiClient from "../services/axios";
import { useTranslation } from "react-i18next";

interface ErrorPageProps {
  message?: string;
}

const ErrorPage: React.FC<ErrorPageProps> = ({ message }) => {
  const refreshPage = () => {
    window.parent.postMessage({ type: "refresh" }, "*");
  };

  const { t } = useTranslation();

  const handleManageKey = () => {
    // Get the Odoo instance URL from the axios baseURL
    const baseURL = apiClient.defaults.baseURL || '';
    // Remove '/api' suffix if present to get the root Odoo URL
    const odooUrl = baseURL.replace(/\/api\/?$/, '');
    // Navigate to the Odoo settings action
    window.parent.location.href = `${odooUrl}/web#action=odashboard.action_odashboard_settings`;
  }

  return (
    <div className="flex items-center justify-center w-full h-screen bg-white">
      <Card className="max-w-3xl w-full border-none shadow-none">
        <CardHeader className="text-center">
          {
            apiClient.defaults.baseURL && apiClient.defaults.baseURL.includes("http://") && (
              <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-lg max-w-2xl mx-auto">
                <div className="flex items-start gap-3 text-left">
                  <div className="bg-amber-100 rounded-full p-2 flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-amber-900 mb-2">
                      {t("home.errorPage.httpWarning.title")}
                    </h3>
                    <p className="text-sm text-amber-800 mb-4 leading-relaxed">
                      {t("home.errorPage.httpWarning.description")}
                    </p>
                    <div className="bg-white border border-amber-200 rounded-md p-4 text-sm">
                      <p className="font-medium text-amber-900 mb-2">{t("home.errorPage.httpWarning.fixTitle")}</p>
                      <ol className="space-y-2 text-amber-800">
                        <li className="flex gap-2">
                          <span className="font-semibold text-amber-900 flex-shrink-0">1.</span>
                          <span dangerouslySetInnerHTML={{ __html: t("home.errorPage.httpWarning.step1") }} />
                        </li>
                        <li className="flex gap-2">
                          <span className="font-semibold text-amber-900 flex-shrink-0">2.</span>
                          <span dangerouslySetInnerHTML={{ __html: t("home.errorPage.httpWarning.step2") }} />
                        </li>
                        <li className="flex gap-2">
                          <span className="font-semibold text-amber-900 flex-shrink-0">3.</span>
                          <span dangerouslySetInnerHTML={{ __html: t("home.errorPage.httpWarning.step3") }} />
                        </li>
                        <li className="flex gap-2">
                          <span className="font-semibold text-amber-900 flex-shrink-0">4.</span>
                          <span>{t("home.errorPage.httpWarning.step4")}</span>
                        </li>
                      </ol>
                    </div>

                    {/* Ngrok Testing Tip - integrated within HTTP warning */}
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 rounded-full p-1.5 flex-shrink-0">
                          <Lightbulb className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-blue-900 text-sm mb-1.5">
                            {t("home.errorPage.ngrokTip.title")}
                          </h4>
                          <p className="text-xs text-blue-800 mb-2 leading-relaxed" dangerouslySetInnerHTML={{ __html: t("home.errorPage.ngrokTip.description") }} />
                          <p className="text-xs text-blue-800 mb-3 leading-relaxed" dangerouslySetInnerHTML={{ __html: t("home.errorPage.ngrokTip.howTo") }} />
                          
                          {/* Demo Instance CTA - More Visible */}
                          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-3 mb-3">
                            <p className="text-xs text-green-900 font-semibold mb-2">
                              {t("home.errorPage.ngrokTip.demoCtaTitle")}
                            </p>
                            <p className="text-xs text-green-800 mb-2.5">
                              {t("home.errorPage.ngrokTip.demoCtaDescription")}
                            </p>
                            <a
                              href="https://demo.odashboard.app/auth/autologin?db=odashboard-demo&login=demo&password=demo&redirect=/odoo"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-md transition-colors"
                            >
                              {t("home.errorPage.ngrokTip.demoCtaButton")}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>

                          <a
                            href={t("home.errorPage.ngrokTip.learnMoreUrl")}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
                          >
                            {t("home.errorPage.ngrokTip.learnMore")}
                            <ExternalLink target="_blank" className="h-3 w-3" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          }
          <div className="mx-auto bg-amber-50 rounded-full w-16 h-16 flex items-center justify-center mb-4">
                <WifiOff className="h-8 w-8 text-amber-500" />
              </div>
          <h2 className="text-2xl font-bold tracking-tight">
            {t("home.errorPage.title")}
          </h2>
          <p className="text-muted-foreground mt-2">
            {t("home.errorPage.description")}
          </p>
          {message && (
            <div className="mt-4 p-3 bg-amber-50 rounded-md text-sm text-amber-800 border border-amber-200">
              {message}
            </div>
          )}
          <p className="text-muted-foreground mt-4 text-sm">
            {t("home.errorPage.description2")}
          </p>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-6 pb-6">
          <Button
            onClick={refreshPage}
            size="lg"
            variant="outline"
            className="px-8 gap-2"
          >
            <RefreshCw className="h-5 w-5" />
              {t("home.errorPage.refresh")}
          </Button>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-2xl">
            <Card className="border border-muted hover:border-primary/50 hover:shadow transition-all cursor-pointer">
              <a
                target="_parent"
                className="block p-4"
                onClick={handleManageKey}
              >
                <div className="flex items-start gap-3">
                  <BookOpen className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <h3 className="font-medium">{t("home.errorPage.manageKey")}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t("home.errorPage.manageKeyDescription")}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
                </div>
              </a>
            </Card>
            <Card className="border border-muted hover:border-primary/50 hover:shadow transition-all cursor-pointer">
              <a
                href="https://odashboard.app/helpdesk/o-dashboard-2"
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4"
              >
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <h3 className="font-medium">{t("home.errorPage.needHelp")}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t("home.errorPage.needHelpDescription")}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
                </div>
              </a>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ErrorPage;
